// FOF home page formatting (spec 2026-10-12 §4.1) — pure, so FofHome stays a thin view.
import type { FofFundRow } from '../../api/client'
import { FUND_REGISTRY, primaryPageHostname, type PageSlug } from '../nav/fundConfig'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** '+0.42%' / '−0.18%' (true minus sign) / '0.00%' / '—' when unknown. */
export function signedPct(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return '—'
  const abs = Math.abs(v).toFixed(2)
  if (Number(abs) === 0) return '0.00%'
  return `${v > 0 ? '+' : '−'}${abs}%`
}

export function moveClass(v: number | null): 'up' | 'down' | 'flat' {
  if (v == null || v === 0) return 'flat'
  return v > 0 ? 'up' : 'down'
}

/** '9 Oct' from an ISO date. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${d} ${MONTHS[m - 1]}`
}

/** 'est.' for a live estimate, 'NAV' for today's official NAV, 'NAV 9 Oct' for an older one. */
export function fundMoveSuffix(row: FofFundRow, todayIso: string): string {
  if (row.fundMoveSource === 'est') return 'est.'
  if (row.fundMoveSource === 'nav') return row.navDate && row.navDate !== todayIso ? `NAV ${shortDate(row.navDate)}` : 'NAV'
  return ''
}

/** 'Invested £12,346' / 'Cash' / '—' (no account data, e.g. a superuser previewing a gated fund). */
export function positionText(row: FofFundRow): string {
  if (row.invested == null) return '—'
  if (!row.invested) return 'Cash'
  return `Invested £${Math.round(row.positionGbp ?? 0).toLocaleString('en-GB')}`
}

/** The signal's date when it is not today's (weekend / before the 08:40 decision), else ''. */
export function signalDateNote(row: FofFundRow, todayIso: string): string {
  return row.signalDate && row.signalDate !== todayIso ? shortDate(row.signalDate) : ''
}

/** Link from a FOF row to the fund's page: ?portfolio= locally, the fund's subdomain otherwise. */
export function rowHref(slug: string, hostname: string): string {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `?portfolio=${slug}`
  // A row the API lists before this build knows the fund degrades to a relative link rather than throwing.
  if (slug !== 'fof' && !(slug in FUND_REGISTRY)) return `?portfolio=${slug}`
  return `https://${primaryPageHostname(slug as PageSlug)}`
}

/** Today's date in London, ISO — the day the API's NAV/signal dates are compared against. */
export function todayLondonIso(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

// ── FOF home redesign 2026-11-02 (spec §4.3 pills, §4.4 position cell) ──────────────────────────────────────────

/** Decision pill class: BUY green, SELL red, any other word grey, nothing ⇒ a grey dash. */
export function decisionPillClass(decision: string | null): 'buy' | 'sell' | 'other' | 'none' {
  if (!decision) return 'none'
  const d = decision.toUpperCase()
  return d === 'BUY' ? 'buy' : d === 'SELL' ? 'sell' : 'other'
}

/** The decision's colour for the mini-chart marks (Paolo 2026-11-03): BUY green, SELL red, anything else grey. */
export function decisionColour(decision: string | null | undefined): string {
  const c = decisionPillClass(decision ?? null)
  return c === 'buy' ? '#4caf50' : c === 'sell' ? '#f44336' : '#9e9e9e'
}

/** BUY rows first, then SELL, then rows with any other or no decision (Paolo 2026-11-03). */
export function decisionRank(decision: string | null | undefined): number {
  const c = decisionPillClass(decision ?? null)
  return c === 'buy' ? 0 : c === 'sell' ? 1 : 2
}

/** 'LIVE' when the fund has weight in the viewer's pot, 'DARK' otherwise, null when the API could not tell. */
export function livePillText(live: boolean | null): 'LIVE' | 'DARK' | null {
  return live == null ? null : live ? 'LIVE' : 'DARK'
}

const gbp = (v: number) => `£${Math.round(v).toLocaleString('en-GB')}`

/** "Bought today · £7,500" / "Sold today · £9,870 → cash" (amount omitted while the fill is unpriced), null when no deal. */
export function dealtTodayText(row: FofFundRow): { text: string; cls: 'up' | 'down' } | null {
  const d = row.dealtToday
  if (!d) return null
  const amount = d.notionalGbp != null && d.notionalGbp > 0 ? ` · ${gbp(d.notionalGbp)}` : ''
  return d.direction === 'BUY'
    ? { text: `Bought today${amount}`, cls: 'up' }
    : { text: `Sold today${amount} → cash`, cls: 'down' }
}

/** Line 3 of the card / the Position column: today's deal when there is one, else the position text. */
export function positionLine(row: FofFundRow): { text: string; cls?: 'up' | 'down' } {
  return dealtTodayText(row) ?? { text: positionText(row) }
}

// ── Row order (Paolo 2026-11-03): live funds above dark ones, grouped by futures family ─────────────────────────────

/** Futures families in display order; a future's family is the one that lists it, anything else is 'Other'. */
export const FUTURE_FAMILIES: ReadonlyArray<{ name: string; futures: readonly string[] }> = [
  { name: 'Equity index', futures: ['NQ', 'ES', 'RTY', 'NIY'] },
  { name: 'Metals', futures: ['GC', 'SI', 'HG'] },
  { name: 'Energy', futures: ['CL'] },
]
const FUTURE_ORDER: readonly string[] = FUTURE_FAMILIES.flatMap(f => f.futures)

export function futureFamily(symbol: string | null | undefined): string {
  const s = (symbol ?? '').toUpperCase()
  return FUTURE_FAMILIES.find(f => f.futures.includes(s))?.name ?? 'Other'
}

/** Rank of a future in the family order; unknown futures sort last. */
export function futureRank(symbol: string | null | undefined): number {
  const i = FUTURE_ORDER.indexOf((symbol ?? '').toUpperCase())
  return i < 0 ? FUTURE_ORDER.length : i
}

/** LIVE rows first, then DARK, then rows whose live flag is unknown. */
export function liveRank(live: boolean | null | undefined): number {
  return live === true ? 0 : live === false ? 1 : 2
}

/** Live above dark; within a section BUY rows then SELL then none (Paolo 2026-11-03 afternoon); within a group by
 * futures family then future (two funds on one future sit together); the API's order breaks ties, so a stable sort. */
export function orderFofRows<T extends Pick<FofFundRow, 'live' | 'futuresSymbol' | 'decision'>>(rows: readonly T[]): T[] {
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => liveRank(a.r.live) - liveRank(b.r.live)
      || decisionRank(a.r.decision) - decisionRank(b.r.decision)
      || futureRank(a.r.futuresSymbol) - futureRank(b.r.futuresSymbol) || a.i - b.i)
    .map(x => x.r)
}

/** For each ordered row: the section heading to print above it ('Dark funds' once, where the live rows end) and
 * whether it starts a new group within its section — a decision-group or futures-family change (a visual boundary). */
export function fofRowBreaks<T extends Pick<FofFundRow, 'live' | 'futuresSymbol' | 'decision'>>(ordered: readonly T[]): { heading: string | null; familyStart: boolean }[] {
  return ordered.map((r, i) => {
    const prev = i > 0 ? ordered[i - 1] : null
    const sectionChange = prev != null && liveRank(prev.live) === 0 && liveRank(r.live) > 0
    const heading = sectionChange ? 'Dark funds' : null
    const familyStart = prev == null || sectionChange
      || decisionRank(prev.decision) !== decisionRank(r.decision)
      || futureFamily(prev.futuresSymbol) !== futureFamily(r.futuresSymbol)
    return { heading, familyStart }
  })
}
